import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { CornerDownLeft, Mic, Send, Sparkles, Trash2, User } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useChat, type UIMessage } from '@ai-sdk/react';
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithApprovalResponses } from 'ai';
import {
  useActiveOrganizationId,
  useHttpClient,
} from '@repo/api-client/react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { describeAppError } from '@/features/common/describeAppError';
import { cn } from '@/lib/utils';
import { aiKeys, useAiConversation } from './hooks';
import {
  extractAllPlans,
  extractAllReminders,
  extractAllStats,
  extractMessageText,
  type UiMessage,
} from './extractArtefacts';
import { VoiceCaptureDialog } from './VoiceCaptureDialog';
import { MarkdownText } from './MarkdownText';
import { PlanCard } from './PlanCard';
import { ReminderCard } from './ReminderCard';
import { StatsCard } from './StatsCard';
import { ThinkingIndicator } from './ThinkingIndicator';

const QUICK_PROMPTS = [
  'Draft a $500 invoice for Acme due Friday',
  'Remind everyone who is overdue',
  'Summarise this month',
];

/**
 * Client-side UUID v4. The server's `ai_conversations.id` column is
 * a UUID, so we mint a real one rather than an opaque token. Maths
 * is fine for our threat model — server enforces tenant scoping; a
 * collision attempt would just fail the insert.
 */
function generateConversationId(): string {
  const hex = '0123456789abcdef';
  const out: string[] = [];
  for (let i = 0; i < 36; i++) {
    if (i === 8 || i === 13 || i === 18 || i === 23) {
      out.push('-');
    } else if (i === 14) {
      out.push('4');
    } else if (i === 19) {
      out.push(hex[8 + Math.floor(Math.random() * 4)]!);
    } else {
      out.push(hex[Math.floor(Math.random() * 16)]!);
    }
  }
  return out.join('');
}

export interface AiChatProps {
  seededPrompt?: string | null;
  seededConversationId?: string | null;
  active: boolean;
  rightActions?: ReactNode;
  fullBleed?: boolean;
  /**
   * Suppress the built-in header (icon + "AI assistant" label). The
   * orb wrapper has its own greeting strip and a duplicate header
   * reads as nesting noise.
   */
  hideHeader?: boolean;
}

/**
 * Headless chat surface for the AI assistant.
 *
 * Built on `@ai-sdk/react`'s `useChat` + `DefaultChatTransport` so
 * web and mobile share the same streaming protocol, tool-part shape,
 * and abort/retry semantics. The earlier manual SSE parser is gone —
 * the SDK handles every part type the server emits (text deltas,
 * tool-input-streaming, tool-output, tool-error, step boundaries)
 * without us tracking the protocol by hand.
 *
 * Renders the assistant tape as a mix of text bubbles and structured
 * artefact cards (PlanCard / ReminderCard / StatsCard). Cards are
 * extracted from each assistant message's `parts` array using the
 * shared walker in `./extractArtefacts.ts` — same code path mobile
 * uses, since `extractAllPlans` etc. only require the UIMessage
 * shape and don't import the SDK directly.
 *
 * Auth: web is cookie-based via SuperTokens. `credentials: 'include'`
 * on the transport's fetch carries the session cookie automatically;
 * we don't need to attach a bearer header the way mobile does.
 */
export function AiChat({
  seededPrompt,
  seededConversationId,
  active,
  rightActions,
  fullBleed = false,
  hideHeader = false,
}: AiChatProps) {
  const orgId = useActiveOrganizationId();
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const inputId = useId();

  // Conversation id is the public-facing chat session identifier and
  // is what the server uses to persist messages. Mint a fresh UUID
  // unless we're resuming.
  const [conversationId, setConversationId] = useState<string>(
    () => seededConversationId ?? generateConversationId(),
  );

  // Resume the persisted message tape when a seed id is provided.
  // The `useChat` initial-message seed happens via `setMessages`
  // after the hook mounts; we read this query then push the messages
  // into useChat once they arrive.
  const { conversation: resumed } = useAiConversation(
    orgId,
    seededConversationId ?? null,
  );

  const baseURL = useMemo(() => http.defaults.baseURL ?? '/api', [http]);

  const transport = useMemo(() => {
    // Always construct an absolute-ish URL. When orgId is null (cold
    // start / sign-in flow) we still need a structurally valid URL
    // so the SDK doesn't throw; the gating on the send button keeps
    // the user from actually triggering a request.
    const safeOrgId = orgId ?? '__no-active-org__';
    return new DefaultChatTransport({
      api: `${baseURL}/orgs/${safeOrgId}/ai/chat`,
      // Cookie-based session via SuperTokens. The browser sends the
      // session cookie automatically when `credentials: 'include'`.
      fetch: ((input, init) =>
        fetch(input, { ...init, credentials: 'include' })) as typeof fetch,
      prepareSendMessagesRequest: async ({ messages, body }) => ({
        body: {
          ...(body as Record<string, unknown> | undefined),
          messages,
          conversationId,
          autoApply: false,
        },
      }),
    });
  }, [baseURL, orgId, conversationId]);

  // The SDK keys its internal Chat by `id` only, not by transport
  // identity. Compose the orgId into the id so a workspace switch
  // mints a fresh Chat (and picks up the new transport) instead of
  // reusing a stale one bound to the old org's URL.
  const chatId = `${orgId ?? '__no-active-org__'}::${conversationId}`;

  const chat = useChat({
    id: chatId,
    transport,
    // After the user taps Accept on a PlanCard, the SDK appends a
    // `tool-approval-response` part to the latest assistant message.
    // This callback tells the SDK to fire the next chat request
    // automatically (otherwise the user would have to type something
    // to resume). The SDK then re-enters the server, runs the
    // approved `createInvoice.execute`, and streams back the resolved
    // tool-output that flips the PlanCard to "Created INV-NNN".
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithApprovalResponses,
  });

  // Hydrate from a resumed conversation. We only push when both the
  // server payload and the chat's id line up with the seed — guards
  // against a workspace switch racing the previous resume.
  const hydratedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!seededConversationId) return;
    if (!resumed) return;
    if (hydratedFor.current === seededConversationId) return;
    chat.setMessages(resumed.messages as UIMessage[]);
    hydratedFor.current = seededConversationId;
  }, [seededConversationId, resumed, chat]);

  // We intentionally do NOT abort the in-flight chat stream when
  // `active` flips false. The orb hides the chat via CSS (not
  // unmount), so the fetch can keep running in the background — the
  // user closing the panel is NOT a cancel signal. Aborting used to
  // kill the request mid-flight, which meant the server's
  // `onFinish` never ran, the assistant turn never persisted, and
  // reopening the panel showed the user's message with no reply.
  // The mic IS stopped on close, though — leaving an open audio
  // capture in the background after the user dismissed the panel
  // would be a privacy own-goal.

  const [input, setInput] = useState('');
  // Track the seed we've already auto-sent so re-renders (or re-opens
  // with the same seed) don't fire it twice. Compose org + seed text +
  // conversation id so a workspace switch or new chat naturally resets
  // — same identity rule the SDK uses for the Chat instance.
  const autoSentRef = useRef<string | null>(null);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!active) return;
    const t = window.setTimeout(() => textareaRef.current?.focus(), 80);
    return () => window.clearTimeout(t);
  }, [active]);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [chat.messages]);

  // Commit state lives entirely on the message tape now. Accept →
  // `addToolApprovalResponse` → SDK auto-resubmits → server runs the
  // tool → resolved `tool-createInvoice` output part lands in
  // `chat.messages` → the walker pairs it with the approval-request
  // and PlanCard flips to its "Created INV-NNN" badge. Reload
  // preserves the badge for free.

  // useChat invalidates the conversation list cache whenever a
  // stream finishes so the sidebar / history page pick up the new
  // turn. Watch the SDK's status transitions.
  const prevStatus = useRef(chat.status);
  useEffect(() => {
    if (prevStatus.current !== 'ready' && chat.status === 'ready' && orgId) {
      void queryClient.invalidateQueries({
        queryKey: aiKeys.conversations(orgId),
      });
      if (seededConversationId) {
        void queryClient.invalidateQueries({
          queryKey: aiKeys.conversation(orgId, seededConversationId),
        });
      }
    }
    prevStatus.current = chat.status;
  }, [chat.status, orgId, seededConversationId, queryClient]);

  // Surface SDK errors as a toast + inline alert. The SDK exposes
  // an Error object on the chat handle; the surrounding screen
  // tear-down (`active === false`) cancels in-flight requests so
  // an abort-error doesn't leak.
  useEffect(() => {
    if (!chat.error) return;
    const description = describeAppError(chat.error).message;
    toast({ title: 'AI request failed', description, variant: 'danger' });
  }, [chat.error, toast]);

  const sendMessage = useCallback(
    (text: string) => {
      const value = text.trim();
      if (!value) return;
      if (!orgId) {
        toast({
          title: 'Pick a business first',
          description: 'AI is scoped to a workspace.',
          variant: 'danger',
        });
        return;
      }
      setInput('');
      void chat.sendMessage({ text: value });
    },
    [orgId, chat, toast],
  );

  // Auto-send a seeded prompt. Every entry point that opens the dock
  // with a `prompt` (omnibox, dashboard suggestions, contextual CTAs)
  // wants the chat to FIRE that turn — not just stage it in the
  // textarea. We guard with an org-scoped key so the same seed can't
  // double-send after a re-render or hot-reload.
  useEffect(() => {
    if (!active) return;
    if (!seededPrompt) return;
    if (!orgId) return;
    const key = `${orgId}:${conversationId}:${seededPrompt}`;
    if (autoSentRef.current === key) return;
    autoSentRef.current = key;
    sendMessage(seededPrompt);
  }, [active, seededPrompt, orgId, conversationId, sendMessage]);

  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      sendMessage(input);
    },
    [input, sendMessage],
  );

  const [voiceOpen, setVoiceOpen] = useState(false);

  // Extract structured artefacts from the message tape. Each extractor
  // returns oldest → newest and anchors entries to the index of the
  // assistant turn that produced them. Older plans render as
  // `superseded` so the user can't commit a stale proposal.
  const uiMessages = chat.messages as unknown as UiMessage[];
  const allPlans = useMemo(() => extractAllPlans(uiMessages), [uiMessages]);
  const allReminders = useMemo(
    () => extractAllReminders(uiMessages),
    [uiMessages],
  );
  const allStats = useMemo(() => extractAllStats(uiMessages), [uiMessages]);

  const onAcceptPlan = useCallback(
    (approvalId: string) => {
      // SDK appends a `tool-approval-response` part to the latest
      // assistant message, then — because of `sendAutomaticallyWhen`
      // — fires a fresh /ai/chat POST. The server resumes the same
      // toolCallId, runs `createInvoice.execute`, and streams the
      // resolved tool-output back. The PlanCard flips automatically
      // once that part lands.
      void chat.addToolApprovalResponse({ id: approvalId, approved: true });
    },
    [chat],
  );

  // For the "Open in editor" handoff we remember which toolCallId the
  // user is waiting on. When the resolved tool-output lands for that
  // call, we navigate to the invoice's edit screen. We keep tracking
  // by toolCallId rather than approvalId because the SDK's
  // toolCallId is stable across the approval-request → resolved-output
  // transition (it's the same call, just in different states), while
  // approvalId is only present in the approval-requested state.
  const editTargetRef = useRef<string | null>(null);
  const onAcceptPlanAndEdit = useCallback(
    (approvalId: string, toolCallId: string) => {
      editTargetRef.current = toolCallId;
      void chat.addToolApprovalResponse({ id: approvalId, approved: true });
    },
    [chat],
  );

  useEffect(() => {
    if (!editTargetRef.current) return;
    const target = editTargetRef.current;
    const match = allPlans.find((p) => p.toolCallId === target && p.resolved);
    if (!match || !match.resolved) return;
    editTargetRef.current = null;
    navigate(`/invoices/${match.resolved.invoiceId}/edit`);
  }, [allPlans, navigate]);

  const onRefinePlan = useCallback(() => {
    textareaRef.current?.focus();
  }, []);

  const newChat = useCallback(() => {
    chat.setMessages([]);
    void chat.stop().catch(() => {});
    setConversationId(generateConversationId());
    hydratedFor.current = null;
  }, [chat]);

  const isStreaming =
    chat.status === 'streaming' || chat.status === 'submitted';
  const messages = chat.messages;

  return (
    <div className="flex h-full min-h-0 flex-col bg-card/95">
      {hideHeader ? null : (
      <header
        className={cn(
          'flex shrink-0 items-center justify-between gap-2 border-b border-line px-4 py-3',
          fullBleed && 'px-6 py-4',
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-accent-soft text-accent">
            <Sparkles className="size-3.5" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">
              AI assistant
            </p>
            <p className="truncate text-[11px] text-muted-foreground">
              {seededConversationId
                ? 'Continuing conversation'
                : 'New chat — try a quick prompt below.'}
            </p>
          </div>
        </div>
        {rightActions}
      </header>
      )}

      <div
        ref={scrollRef}
        className={cn(
          'scrollbar-thin flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-4',
          fullBleed && 'mx-auto w-full max-w-3xl px-6 py-8',
        )}
      >
        {messages.length === 0 ? (
          <EmptyState
            onPickPrompt={(p) => {
              setInput(p);
              textareaRef.current?.focus();
            }}
          />
        ) : (
          messages.map((m, idx) => {
            const messageText = extractMessageText(m as unknown as UiMessage);
            const planAtIdx = allPlans.find((p) => p.messageIndex === idx);
            const isLatestPlan =
              planAtIdx !== undefined &&
              allPlans[allPlans.length - 1]?.messageIndex === idx;
            const reminderAtIdx = allReminders.find(
              (r) => r.messageIndex === idx,
            );
            const isLatestReminder =
              reminderAtIdx !== undefined &&
              allReminders[allReminders.length - 1]?.messageIndex === idx;
            const statsAtIdx = allStats.find((s) => s.messageIndex === idx);
            const role: 'user' | 'assistant' =
              m.role === 'user' ? 'user' : 'assistant';
            const isStreamingThisMessage =
              isStreaming && idx === messages.length - 1 && role === 'assistant';
            return (
              <div key={m.id} className="flex flex-col gap-2">
                {messageText.length > 0 ? (
                  <Message
                    role={role}
                    text={messageText}
                    streaming={isStreamingThisMessage}
                  />
                ) : null}
                {planAtIdx ? (
                  <div className="ml-9 max-w-[640px]">
                    <PlanCard
                      input={planAtIdx.input}
                      resolved={planAtIdx.resolved}
                      onAccept={() => {
                        // Only valid while the SDK is in
                        // `approval-requested` state — `approvalId`
                        // is non-null exactly then.
                        if (planAtIdx.approvalId) {
                          onAcceptPlan(planAtIdx.approvalId);
                        }
                      }}
                      onAcceptAndEdit={
                        planAtIdx.approvalId
                          ? () => {
                              onAcceptPlanAndEdit(
                                planAtIdx.approvalId!,
                                planAtIdx.toolCallId,
                              );
                            }
                          : undefined
                      }
                      onRefine={onRefinePlan}
                      busy={isStreaming && isLatestPlan}
                      superseded={!isLatestPlan && !planAtIdx.resolved}
                      onOpenInvoice={(id) => navigate(`/invoices/${id}`)}
                    />
                  </div>
                ) : null}
                {reminderAtIdx ? (
                  <div className="ml-9 max-w-[640px]">
                    <ReminderCard
                      reminder={reminderAtIdx.artefact}
                      superseded={!isLatestReminder}
                    />
                  </div>
                ) : null}
                {statsAtIdx ? (
                  <div className="ml-9 max-w-[640px]">
                    <StatsCard stats={statsAtIdx.artefact} />
                  </div>
                ) : null}
              </div>
            );
          })
        )}
        {(() => {
          // Show the thinking indicator whenever a turn is in flight
          // but the assistant hasn't put anything on screen yet.
          // "Nothing on screen" = the latest message is the user's,
          // OR the latest message is the assistant but has no visible
          // text/tool-card content. Either way the user is staring
          // at a void waiting for the first byte; a 5s-rotating
          // phrase keeps them oriented.
          if (!isStreaming) return null;
          const last = messages[messages.length - 1];
          if (!last || last.role === 'user') {
            return (
              <div className="ml-1">
                <ThinkingIndicator />
              </div>
            );
          }
          // Latest is assistant — check whether it has rendered
          // anything yet. If yes, the streaming cursor on the bubble
          // already conveys "writing", so skip the indicator.
          const lastText = extractMessageText(last as unknown as UiMessage);
          const latestPlan = allPlans[allPlans.length - 1];
          const latestReminder = allReminders[allReminders.length - 1];
          const latestStats = allStats[allStats.length - 1];
          const lastIdx = messages.length - 1;
          const hasArtefact =
            latestPlan?.messageIndex === lastIdx ||
            latestReminder?.messageIndex === lastIdx ||
            latestStats?.messageIndex === lastIdx;
          if (lastText.length === 0 && !hasArtefact) {
            return (
              <div className="ml-1">
                <ThinkingIndicator />
              </div>
            );
          }
          return null;
        })()}
        {chat.error ? (
          <Alert variant="danger">
            {describeAppError(chat.error).message}
          </Alert>
        ) : null}
      </div>

      <VoiceCaptureDialog
        open={voiceOpen}
        onOpenChange={setVoiceOpen}
        onUse={(text) => sendMessage(text)}
      />
      <form
          onSubmit={onSubmit}
          className={cn(
            'flex shrink-0 flex-col gap-2 border-t border-line bg-card/95 px-4 py-3',
            fullBleed && 'mx-auto w-full max-w-3xl px-6 py-4',
          )}
        >
          <div className="flex items-end gap-2">
            <label htmlFor={inputId} className="sr-only">
              Message
            </label>
            <Textarea
              id={inputId}
              ref={textareaRef}
              rows={2}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask the assistant anything…"
              className="flex-1 resize-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage(input);
                }
              }}
              disabled={isStreaming}
            />
            <div className="flex flex-col gap-1">
              {typeof window !== 'undefined' &&
              ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) ? (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setVoiceOpen(true)}
                  aria-label="Start voice capture"
                  disabled={isStreaming}
                >
                  <Mic className="size-4" />
                </Button>
              ) : null}
              <Button
                type="submit"
                disabled={isStreaming || !input.trim()}
                size="icon"
                aria-label="Send"
              >
                <Send className="size-4" />
              </Button>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <kbd className="rounded border border-line-strong px-1 py-px font-mono text-[10px]">
                <CornerDownLeft className="inline-block size-2.5" />
              </kbd>
              send · <kbd className="rounded border border-line-strong px-1 py-px font-mono text-[10px]">⇧↵</kbd>{' '}
              newline
            </span>
            {messages.length > 0 ? (
              <button type="button" onClick={newChat} className="inline-flex items-center gap-1 hover:text-foreground">
                <Trash2 className="size-3" /> Clear
              </button>
            ) : null}
          </div>
        </form>
    </div>
  );
}

function EmptyState({ onPickPrompt }: { onPickPrompt: (p: string) => void }) {
  return (
    <div className="flex flex-col items-stretch gap-4 px-1 pt-2 text-left">
      <div className="rounded-xl border border-line bg-secondary/30 p-4">
        <p className="text-sm font-medium text-foreground">
          Tell me what to do — I can draft, send, or summarise.
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          I see your active business, your clients, your invoices and your
          defaults — so prompts can be very short.
        </p>
      </div>
      <div className="space-y-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          Quick start
        </p>
        <div className="flex flex-col gap-1.5">
          {QUICK_PROMPTS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => onPickPrompt(p)}
              className="group flex items-center justify-between rounded-md border border-line bg-card px-3 py-2 text-left text-sm text-foreground hover:border-accent/40 hover:bg-card"
            >
              <span className="truncate">{p}</span>
              <CornerDownLeft className="size-3.5 text-muted-foreground group-hover:text-accent" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Message({
  role,
  text,
  streaming,
}: {
  role: 'user' | 'assistant';
  text: string;
  streaming?: boolean;
}) {
  const isUser = role === 'user';
  return (
    <div className={cn('flex gap-2.5', isUser ? 'flex-row-reverse' : '')}>
      <div
        className={cn(
          'flex size-7 shrink-0 items-center justify-center rounded-full',
          isUser
            ? 'bg-foreground text-background'
            : 'bg-accent-soft text-accent',
        )}
      >
        {isUser ? <User className="size-3.5" /> : <Sparkles className="size-3.5" />}
      </div>
      <div
        className={cn(
          'max-w-[82%] rounded-2xl px-3 py-2 text-sm leading-relaxed',
          isUser
            ? 'bg-foreground text-background'
            : 'border border-line bg-card text-foreground',
        )}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap">
            {text}
            {streaming ? <span className="ml-0.5 animate-pulse">▋</span> : null}
          </p>
        ) : (
          <MarkdownText content={text} />
        )}
        {!isUser && streaming ? (
          <span className="ml-0.5 animate-pulse text-muted-foreground">▋</span>
        ) : null}
      </div>
    </div>
  );
}
